/**
 * Feature flags / entitlement checks.
 *
 * Centralised so we can later wire these to the auth store (subscription
 * level, org role, A/B bucket, etc.) without chasing call sites across the
 * app. Every gate should be a hook or a plain function — hooks when the
 * check needs to react to store changes, plain functions when it doesn't.
 *
 * Current behaviour: everything returns true so UI surfaces are visible
 * during development. Replace the body of each check when the real
 * entitlement source lands.
 */

/**
 * Marketplace is intended to be gated by subscription tier (exact tier
 * TBD). For now every user sees it; when entitlements ship, route this
 * through `useAuthStore` / a subscription selector and have it return
 * false for users on the free tier (or whatever the final rule is).
 *
 * Kept as a hook so a future implementation can subscribe to store
 * updates without forcing callers to refactor. Consumers should treat a
 * `false` return as "hide the entry point entirely" — don't show a
 * disabled state, since that would leak the existence of a paywalled
 * feature to users who can't use it.
 */
export function useCanAccessMarketplace(): boolean {
  return true;
}

/**
 * Gate for sharing a bot publicly (the "Share" action in the bot
 * detail ⋯ menu — public link + "anyone with the link can view"
 * toggle). In WHB this is
 * intended to be an entitlement-gated capability (Sergey 2026-05-13:
 * "Marketplace, Share are only available/enablable" for specific
 * users / subscription levels).
 *
 * Same contract as `useCanAccessMarketplace`: a `false` return means
 * "hide the Share entry point entirely" rather than showing a
 * disabled state. Returns `true` during development until the real
 * entitlement source is wired through `useAuthStore`.
 */
export function useCanShareBot(): boolean {
  return true;
}

/**
 * Gate for the "Log in with real WHB account" flow (see
 * docs/REAL-LOGIN-RECON.md). The real-login flow is now in daily use by
 * Sergey, so the default is `true`. The env var and query-param overrides
 * remain so the flag can still be turned OFF (e.g. `?realLogin=0`) if a
 * deployment needs to pull the UI surface back temporarily.
 */
export function useWhbRealLoginEnabled(): boolean {
  // Env flag, set at build time. Accepts "1" / "true" to force on,
  // "0" / "false" to force off.
  const envRaw = import.meta.env.VITE_WHB_REAL_LOGIN as string | undefined;
  if (envRaw === "0" || envRaw === "false") return false;
  if (envRaw === "1" || envRaw === "true") return true;

  // Query-param override — `?realLogin=0` turns the flag off for the
  // current tab, `?realLogin=1` forces it on (no-op now that default is
  // true, but kept for symmetry).
  if (typeof window !== "undefined") {
    try {
      const q = new URLSearchParams(window.location.search).get("realLogin");
      if (q === "0" || q === "false") return false;
      if (q === "1" || q === "true") return true;
    } catch {
      /* fall through to default */
    }
  }

  // Default on — real accounts are the primary audience now.
  return true;
}
